import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUsers, FiShoppingBag, FiTrendingUp, FiCalendar, FiAward, FiTrendingDown, FiRefreshCw, FiArrowUp, FiMapPin, FiBarChart2, FiShield, FiHome, FiSearch } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { FaRupeeSign } from "react-icons/fa";

const EnhancedDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('today');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [showDateRange, setShowDateRange] = useState(false);
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [roomVendorSearchTerm, setRoomVendorSearchTerm] = useState('');
  const [debouncedVendorSearch, setDebouncedVendorSearch] = useState('');
  const [debouncedRoomVendorSearch, setDebouncedRoomVendorSearch] = useState('');

  useEffect(() => {
    fetchDashboardStats();
  }, [filterType, dateRange]);

  // Debounced vendor search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedVendorSearch(vendorSearchTerm);
    }, 1000); // 1 second delay

    return () => clearTimeout(timer);
  }, [vendorSearchTerm]);

  // Debounced room vendor search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedRoomVendorSearch(roomVendorSearchTerm);
    }, 1000); // 1 second delay

    return () => clearTimeout(timer);
  }, [roomVendorSearchTerm]);

  // Trigger API call when vendor search changes
  useEffect(() => {
    if (debouncedVendorSearch !== undefined) {
      fetchDashboardStats();
    }
  }, [debouncedVendorSearch]);

  // Trigger API call when room vendor search changes
  useEffect(() => {
    if (debouncedRoomVendorSearch !== undefined) {
      fetchDashboardStats();
    }
  }, [debouncedRoomVendorSearch]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const params = { filterType };
      if (filterType === 'custom' && dateRange.startDate && dateRange.endDate) {
        params.startDate = dateRange.startDate;
        params.endDate = dateRange.endDate;
      }
      
      // Add search parameters if they exist
      if (debouncedVendorSearch) {
        params.vendorSearch = debouncedVendorSearch;
      }
      if (debouncedRoomVendorSearch) {
        params.roomVendorSearch = debouncedRoomVendorSearch;
      }

      const response = await api.get('/superadmin/dashboard/stats', { params });
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch dashboard stats');
    } finally {
      setLoading(false);
    }
  };

const StatCard = ({
  icon: Icon,
  title,
  value,
  subtitle,
  color,
  onClick,
  badges = [],
}) => (
  <div
    className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
  >
    {/* subtle gradient glow */}
    <div className={`absolute inset-0 opacity-0 group-hover:opacity-10 transition bg-gradient-to-br ${color}`}></div>

    <div className="relative p-4">

      {/* top row */}
      <div className="flex items-center justify-between mb-3">

        {/* icon */}
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center shadow-sm`}>
          <Icon className="text-white text-lg" />
        </div>
      </div>

      {/* value */}
      <div>
        <h3 className="text-2xl font-bold text-gray-900 leading-none">
          {value}
        </h3>

        <p className="text-xs font-medium text-gray-600 mt-1">
          {title}
        </p>

        {subtitle && (
          <p className="text-[11px] text-gray-400">
            {subtitle}
          </p>
        )}
      </div>

      {/* badges */}
      {badges.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {badges.map((badge, index) => (
            <span
              key={index}
              className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${badge.color}`}
            >
              {badge.label}
            </span>
          ))}
        </div>
      )}

    </div>
  </div>
);

  const FilterButton = ({ label, isActive, onClick, icon: Icon }) => (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
        isActive
          ? 'bg-indigo-600 text-white shadow-lg'
          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
      }`}
    >
      {Icon && <Icon className="text-sm" />}
      {label}
    </button>
  );

  // Filter vendors based on search term
  const getFilteredVendors = useCallback((vendors) => {
    if (!debouncedVendorSearch) return vendors;
    
    const searchLower = debouncedVendorSearch.toLowerCase();
    return vendors.filter(vendor => 
      vendor.restaurantName?.toLowerCase().includes(searchLower) ||
      vendor.ownerName?.toLowerCase().includes(searchLower) ||
      vendor.city?.toLowerCase().includes(searchLower) ||
      vendor.totalRevenue?.toString().includes(searchLower)
    );
  }, [debouncedVendorSearch]);

  // Filter room vendors based on search term
  const getFilteredRoomVendors = useCallback((vendors) => {
    if (!debouncedRoomVendorSearch) return vendors;
    
    const searchLower = debouncedRoomVendorSearch.toLowerCase();
    return vendors.filter(vendor => 
      vendor.hotelName?.toLowerCase().includes(searchLower) ||
      vendor.ownerName?.toLowerCase().includes(searchLower) ||
      vendor.city?.toLowerCase().includes(searchLower) ||
      vendor.totalRevenue?.toString().includes(searchLower) ||
      vendor.totalBookings?.toString().includes(searchLower)
    );
  }, [debouncedRoomVendorSearch]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-flex items-center justify-center">
            <div className="w-20 h-20 border-4 border-indigo-200 rounded-full"></div>
            <div className="w-20 h-20 border-4 border-indigo-600 rounded-full animate-spin absolute border-t-transparent"></div>
          </div>
          <p className="mt-4 text-gray-600 font-medium">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <div className="w-10 h-5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <FiShield className="text-white text-sm" />
                </div>
                Enhanced Dashboard
              </h1>
              <p className="text-gray-600 mt-1">Complete platform overview and management</p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={fetchDashboardStats}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200"
              >
                <FiRefreshCw className="text-sm" />
                <span className="text-sm font-medium">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-0 py-2">
        {/* Filter Section */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-2 mb-3">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <FilterButton
                label="Today"
                isActive={filterType === 'today'}
                onClick={() => setFilterType('today')}
              />
              <FilterButton
                label="Yesterday"
                isActive={filterType === 'yesterday'}
                onClick={() => setFilterType('yesterday')}
              />
              <FilterButton
                label="This Week"
                isActive={filterType === 'week'}
                onClick={() => setFilterType('week')}
              />
              <FilterButton
                label="This Month"
                isActive={filterType === 'month'}
                onClick={() => setFilterType('month')}
              />
              <FilterButton
                label="Custom Range"
                isActive={filterType === 'custom'}
                onClick={() => {
                  setFilterType('custom');
                  setShowDateRange(!showDateRange);
                }}
                icon={FiCalendar}
              />
            </div>
          </div>

          {showDateRange && filterType === 'custom' && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">From:</label>
                  <input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">To:</label>
                  <input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Order Statistics */}
        <div className="mb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 !gap-2">
            <StatCard
              icon={FiShoppingBag}
              title="Total Orders"
              value={stats?.orders?.completed || 0}
              subtitle="Completed orders"
              color="from-blue-500 to-blue-600"
              onClick={() => navigate('/superadmin/orders')}
            />

            <StatCard
              icon={FiUsers}
              title="Total Vendors"
              value={stats?.vendors?.total || 0}
              subtitle="Registered restaurants"
              color="from-indigo-500 to-indigo-600"
              onClick={() => navigate('/superadmin/vendors')}
              badges={[
                { label: `${stats?.vendors?.active || 0} Active`, color: 'bg-green-100 text-green-700' },
                { label: `${stats?.vendors?.pending || 0} Pending`, color: 'bg-amber-100 text-amber-700' }
              ]}
              trend={{ type: 'up', value: '8%' }}
            />

            <StatCard
              icon={FiUsers}
              title="App Users"
              value={stats?.customers?.total || 0}
              subtitle={`${stats?.customers?.new || 0} new users`}
              color="from-purple-500 to-purple-600"
              onClick={() => navigate('/superadmin/app-users')}
              trend={{ type: 'up', value: '15%' }}
            />

            <StatCard
              icon={FaRupeeSign}
              title="Total Order Revenue"
              value={`₹${(stats?.orders?.revenue || 0).toLocaleString()}`}
              subtitle="Platform revenue"
              color="from-green-500 to-green-600"
            />
            <StatCard
              icon={FiHome}
              title="Total Room Revenue"
              value={`₹${(stats?.roomRevenue?.totalRevenue || 0).toLocaleString()}`}
              subtitle={`${stats?.roomRevenue?.totalBookings || 0} room bookings`}
              color="from-emerald-500 to-emerald-600"
              onClick={() => navigate('/superadmin/rooms')}
            />

            <StatCard
              icon={FiCalendar}
              title="Total Banquet Revenue"
              value={`₹${(stats?.banquetRevenue?.totalRevenue || 0).toLocaleString()}`}
              subtitle={`${stats?.banquetRevenue?.totalBookings || 0} banquet bookings`}
              color="from-purple-500 to-purple-600"
              onClick={() => navigate('/superadmin/banquets')}
            />
          </div>
        </div>

        {/* Top 10 Vendors by Revenue */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <div className="flex flex-wrap items-center justify-between gap-4">

              {/* Left Section */}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-indigo-100">
                  <FiAward className="text-indigo-600 text-lg" />
                </div>

                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Top 10 Vendors by Revenue
                  </h2>
                  <p className="text-xs text-gray-500">
                    Best performing vendors
                  </p>
                </div>
              </div>

              {/* Right Section */}
              <div className="flex items-center gap-3">

                {/* Search */}
                <div className="relative w-64 md:w-80 lg:w-[420px]">
                  <FiSearch className="absolute left-3 top-2.5 text-gray-400 text-sm" />
                  <input
                    type="text"
                    placeholder="Search vendors..."
                    value={vendorSearchTerm}
                    onChange={(e) => setVendorSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm 
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 
                    transition-all duration-200"
                  />
                </div>

              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Restaurant Name</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner Name</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Orders</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Revenue</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Order Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(() => {
                  const filteredVendors = getFilteredVendors(stats?.topVendors || []);
                  return filteredVendors && filteredVendors.length > 0 ? (
                    filteredVendors.map((vendor, index) => (
                      <tr key={vendor.vendorId} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {index < 3 && (
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                index === 0 ? 'bg-yellow-100 text-yellow-600' :
                                index === 1 ? 'bg-gray-100 text-gray-600' :
                                'bg-orange-100 text-orange-600'
                              }`}>
                                {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                              </div>
                            )}
                            <span className={`font-bold ${
                              index < 3 ? 'text-amber-600 text-lg' : 'text-gray-600'
                            }`}>
                              {index + 1}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                              <FiMapPin className="text-indigo-600 text-sm" />
                            </div>
                            <span className="font-semibold text-gray-900">{vendor.restaurantName || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-700">{vendor.ownerName || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            {vendor.city || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold">
                            {vendor.totalOrders}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <FiTrendingUp className="text-green-500 text-sm" />
                            <span className="font-bold text-green-600">
                              ₹{(vendor.totalRevenue || 0).toLocaleString()}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                          ₹{Math.round(vendor.averageOrderValue || 0).toLocaleString()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                            <FiBarChart2 className="text-gray-400 text-2xl" />
                          </div>
                          <p className="text-gray-500 font-medium">
                            {debouncedVendorSearch ? 'No vendors found matching your search' : 'No vendor data available'}
                          </p>
                          <p className="text-gray-400 text-sm">
                            {debouncedVendorSearch ? 'Try a different search term' : 'Try selecting a different time period'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top 10 Vendors by Room Revenue */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 mt-2">
          <div className="p-3 border-b border-gray-100">
            <div className="flex flex-wrap items-center justify-between gap-4">

              {/* Left Section */}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-green-100">
                  <FiHome className="text-green-600 text-lg" />
                </div>

                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Top 10 Vendors by Room Revenue
                  </h2>
                  <p className="text-xs text-gray-500">
                    Room booking performers
                  </p>
                </div>
              </div>

              {/* Right Section */}
              <div className="flex items-center gap-3">

                {/* Search */}
                <div className="relative w-64 md:w-80 lg:w-[420px]">
                  <FiSearch className="absolute left-3 top-2.5 text-gray-400 text-sm" />
                  <input
                    type="text"
                    placeholder="Search room vendors..."
                    value={roomVendorSearchTerm}
                    onChange={(e) => setRoomVendorSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm shadow-sm hover:shadow-md
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Restaurant Name</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner Name</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Booking</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Room Revenue</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Room Price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(() => {
                  const filteredRoomVendors = getFilteredRoomVendors(stats?.topVendorsByRoomRevenue || []);
                  return filteredRoomVendors && filteredRoomVendors.length > 0 ? (
                    filteredRoomVendors.map((vendor, index) => (
                      <tr key={vendor.vendorId} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {index < 3 && (
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                index === 0 ? 'bg-yellow-100 text-yellow-600' :
                                index === 1 ? 'bg-gray-100 text-gray-600' :
                                'bg-orange-100 text-orange-600'
                              }`}>
                                {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                              </div>
                            )}
                            <span className={`font-bold ${
                              index < 3 ? 'text-amber-600 text-lg' : 'text-gray-600'
                            }`}>
                              {index + 1}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                              <FiHome className="text-green-600 text-sm" />
                            </div>
                            <span className="font-semibold text-gray-900">{vendor.hotelName || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-700">{vendor.ownerName || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            {vendor.city || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                            {vendor.totalBookings || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <FaRupeeSign className="text-green-500 text-sm" />
                            <span className="font-bold text-green-600">
                              {(vendor.totalRevenue || 0).toLocaleString()}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                          <FaRupeeSign className="inline text-gray-400 text-xs mr-1" />
                          {Math.round(vendor.averageBookingValue || 0).toLocaleString()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                            <FiHome className="text-gray-400 text-2xl" />
                          </div>
                          <p className="text-gray-500 font-medium">
                            {debouncedRoomVendorSearch ? 'No room vendors found matching your search' : 'No room revenue data available'}
                          </p>
                          <p className="text-gray-400 text-sm">
                            {debouncedRoomVendorSearch ? 'Try a different search term' : 'Try selecting a different time period'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedDashboard;
